import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useRef, Suspense } from "react";
import { lazyWithRetry as lazy } from "@/lib/lazy-with-retry";
import { AuthProvider } from "@/contexts/AuthContext";
import { EditModeProvider } from "@/contexts/EditModeContext";
import RGPDConsent from "@/components/RGPDConsent";
import { useTrackingScripts } from "@/hooks/use-tracking-scripts";
import { usePWAManifest } from "@/hooks/use-pwa-manifest";
import { useFaviconTitle } from "@/hooks/use-favicon-title";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

// Lazy-loaded routes (code-splitting)
const AdminSetup = lazy(() => import("./pages/AdminSetup"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const Agenda = lazy(() => import("./pages/admin/Agenda"));
const ServicesAdmin = lazy(() => import("./pages/admin/ServicesAdmin"));
const Finances = lazy(() => import("./pages/admin/Finances"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const ApiSettings = lazy(() => import("./pages/admin/ApiSettings"));
const ContentManager = lazy(() => import("./pages/admin/ContentManager"));
const ProductsAdmin = lazy(() => import("./pages/admin/ProductsAdmin"));
const OrdersAdmin = lazy(() => import("./pages/admin/OrdersAdmin"));
const StaffAdmin = lazy(() => import("./pages/admin/StaffAdmin"));
const StaffPortal = lazy(() => import("./pages/staff/StaffPortal"));
const Store = lazy(() => import("./pages/Store"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const PDV = lazy(() => import("./pages/admin/PDV"));
const PackagesAdmin = lazy(() => import("./pages/admin/PackagesAdmin"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CertificatesPage = lazy(() => import("./pages/CertificatesPage"));
const ClientRecord = lazy(() => import("./pages/ClientRecord"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const ClientsAdmin = lazy(() => import("./pages/admin/ClientsAdmin"));
const ClientRecordFormAdmin = lazy(() => import("./pages/admin/ClientRecordFormAdmin"));
const Reports = lazy(() => import("./pages/admin/Reports"));


const queryClient = new QueryClient();

const AppWithTracking = () => {
  useTrackingScripts();
  return null;
};

const PWAManifestSwitcher = () => {
  usePWAManifest();
  return null;
};

const GlobalFavicon = () => {
  useFaviconTitle();
  return null;
};


const ScrollToHash = () => {
  const { pathname, hash, search } = useLocation();
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      if (hash) {
        window.history.replaceState(null, "", `${pathname}${search}`);
      }
      return;
    }

    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [pathname, hash, search]);

  return null;
};

const RGPDConsentWrapper = () => {
  const { pathname } = useLocation();
  const isAdminOrPdv = pathname.startsWith("/admin") || pathname.startsWith("/pdv") || pathname.startsWith("/colaborador");
  if (isAdminOrPdv) return null;
  return <RGPDConsent />;
};

const LazyFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <EditModeProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToHash />
            <PWAManifestSwitcher />
            <GlobalFavicon />
            <RGPDConsentWrapper />
            <AppWithTracking />
            <Suspense fallback={<LazyFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/politica-privacidade" element={<PrivacyPolicy />} />
                <Route path="/servicos" element={<ServicesPage />} />
                <Route path="/certificados" element={<CertificatesPage />} />

                <Route path="/ficha" element={<ClientRecord />} />
                <Route path="/minha-conta" element={<ClientPortal />} />

                <Route path="/loja" element={<Store />} />
                <Route path="/admin-setup" element={<AdminSetup />} />
                <Route path="/colaborador" element={<StaffPortal />} />
                <Route path="/pdv" element={<PDV />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="agenda" element={<Agenda />} />
                  <Route path="services" element={<ServicesAdmin />} />
                  <Route path="packages" element={<PackagesAdmin />} />
                  <Route path="products" element={<ProductsAdmin />} />
                  <Route path="orders" element={<OrdersAdmin />} />
                  <Route path="staff" element={<StaffAdmin />} />
                  <Route path="clients" element={<ClientsAdmin />} />
                  <Route path="client-form" element={<ClientRecordFormAdmin />} />
                  <Route path="content" element={<ContentManager />} />
                  <Route path="finances" element={<Finances />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="api" element={<ApiSettings />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </EditModeProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
